import React, { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuthProvider } from "../../Providers/AuthProvider";
import { FRONTEND_LINKS } from "../../Constants/FrontendLinks";
import styled from "styled-components";
import { MobergFontFamily } from "../../Components/MobergFont/MobergFont"
import bgImg from "../Account/Login/bgImg.png";
import darkLogoImg from "../Account/Login/moberg-logo-dark.png";
import lightLogoImg from "../Account/Login/moberg-logo-light.png"
import { useMobergTheme } from "../../Providers/MobergThemeProvider"

const ErrorPage = ({ setNavPage }) => {
    const authProvider = useAuthProvider()
    const { colorScheme } = useMobergTheme()
    const logoImg = colorScheme === "dark" ? lightLogoImg : darkLogoImg

    useEffect(() => {
        setNavPage(false)
    }, [setNavPage])

    if (!authProvider.userIsLoggedIn) {
        return <Navigate to={FRONTEND_LINKS.MAIN} replace />
    }

    return (
        <ErrorWrapper>
            <CenteredErrorBox>
                <ErrorBox>
                    <TopSection>
                        <img src={logoImg} alt="Moberg Analytics" style={{ width: '90px', marginBottom: '16px' }} />
                        <ErrorHeading>Page Not Found</ErrorHeading>
                    </TopSection>
                    <CenterSection>
                        <ErrorCode>404</ErrorCode>
                        <ErrorSubheading>We couldn't find the page you're looking for</ErrorSubheading>
                        <ErrorDescription>
                            The page may have been moved, deleted, or you may have entered the wrong URL.
                        </ErrorDescription>
                    </CenterSection>
                    <BottomSection>
                        <HomeButton to={FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN}>
                            Back to dashboard
                        </HomeButton>
                    </BottomSection>
                </ErrorBox>
            </CenteredErrorBox>
        </ErrorWrapper>
    )
}

const ErrorWrapper = styled.div`
    background-image: url(${bgImg});
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    background-color: var(--color-surface-primary);
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0);
        [data-color-scheme="dark"] & {
            background: rgba(0, 0, 0, 0.75);
        }
    }

    & > * {
        position: relative;
        z-index: 1;
    }
`;

const CenteredErrorBox = styled.div`
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: center;
    justify-self: center;
    height: auto;
    width: 100%;
    max-width: 450px;
    min-width: 400px;
    padding: 0 20px;
    margin: 0 auto;

    @media (max-width: 768px) {
        width: 90%;
        min-width: 280px;
        max-width: 400px;
    }

    @media (max-width: 480px) {
        width: 95%;
        min-width: 260px;
        max-width: 350px;
        padding: 0 10px;
    }
`;

const ErrorBox = styled.div`
    box-shadow: var(--modal-box-shadow);
    background: var(--color-surface-panel);
    border-radius: 4px;
    min-width: 400px;
    padding: 48px 32px;
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const TopSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    @media (max-width: 600px) {
        align-items: center;
        padding-top: 0;
        padding-bottom: 16px;
    }
`;

const CenterSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 0;
    text-align: center;
    gap: 16px;
`;

const BottomSection = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ErrorHeading = styled.h1`
    font-family: ${MobergFontFamily.HEADER};
    font-weight: 700;
    margin: 0;
    letter-spacing: 1px;
    font-size: 28px;
    color: var(--color-text-primary);
    text-align: center;
`;

const ErrorCode = styled.div`
    font-family: ${MobergFontFamily.HEADER};
    font-weight: 800;
    font-size: 80px;
    color: var(--color-accent-interactive);
    margin: 0;
    line-height: 1;
`;

const ErrorSubheading = styled.h2`
    font-family: ${MobergFontFamily.HEADER};
    color: var(--color-accent-interactive);
    margin: 0;
    letter-spacing: 1px;
    font-size: 22px;
    font-weight: 600;
    text-align: center;
    align-self: center;
`;

const ErrorDescription = styled.p`
    font-family: ${MobergFontFamily.REGULAR};
    color: var(--text-secondary-color);
    margin: 0;
    font-size: 16px;
    line-height: 1.6;
    max-width: 320px;
    text-align: center;
`;

const HomeButton = styled(Link)`
    width: 100%;
    padding: 12px 0;
    background: var(--color-accent-interactive);
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    font-family: ${MobergFontFamily.HEADER};
    border: none;
    border-radius: 4px;
    cursor: pointer;
    text-decoration: none;
    text-align: center;
    transition: background 0.2s;
    &:hover {
        filter: brightness(0.85);
        text-decoration: none;
        color: #fff;
    }
`;

export default ErrorPage;
